/**
 * Maps the gateway's public catalog (one entry per SKU) into the per-platform
 * cards the catalog page renders. SKUs are grouped by their slug prefix
 * (`tiktok.profile`, `tiktok.profile_videos` → one "TikTok" card), with the
 * card's "from" price = the lowest authoritative `pricing.from` value across
 * the platform's endpoints.
 */

import type {
  ApiDetail,
  CatalogApi,
  CatalogOutcome,
  Category,
  CategoryId,
  Endpoint,
  ProviderQuote,
} from '../features/catalog/model';
import type { CatalogSearchResult } from '../data/catalogSearch';
import type { DiscoveryApi, DiscoveryLane } from '../data/discovery';
import type { EditorialApiDetail } from '../data/editorial';
import { CATS, CCOLOR, CNAME } from '../data/categories';
import { ENRICHMENT_PLATFORM_IDS } from '../data/dataCapabilities';
import { resolveCatalogOutcomes } from './catalogCapabilities';
import {
  actionOf,
  endpointIdOf,
  platformIdOf,
  platformKey,
  platformKeyForRoute,
  platformMeta,
  titleCase,
} from './catalogPlatform';
import { displayPrice, type DisplayPrice } from './pricing';
import { sourcesForEntries } from './providerIdentity';
import { hasFailover } from './routing';
import { catalogText } from '../i18n/catalogText';
import { m } from '../paraglide/messages';

/** One SKU as returned by GET /catalog. */
export type RawCatalogEntry = DiscoveryApi;

interface HeadlineQuote {
  price: DisplayPrice;
  slug: string;
}

const headlineQuotes = (e: RawCatalogEntry): HeadlineQuote[] => [
  { price: displayPrice(e.pricing.from), slug: e.slug },
];

/** Cheapest per request wins. A flat offer beats a metered one at the same
 *  figure, because the metered figure is only a ceiling. */
const compareHeadlineQuotes = (a: HeadlineQuote, b: HeadlineQuote): number => {
  if (a.price.perRequestUsd !== b.price.perRequestUsd)
    return a.price.perRequestUsd - b.price.perRequestUsd;
  if (a.price.metered !== b.price.metered) return a.price.metered ? 1 : -1;
  return a.slug.localeCompare(b.slug);
};

const lowestHeadlineQuote = (skus: RawCatalogEntry[]): HeadlineQuote =>
  skus
    .flatMap(headlineQuotes)
    .reduce((best, quote) => (compareHeadlineQuotes(quote, best) < 0 ? quote : best));

/** Requests one lane successfully served in the gateway's health window; zero
 *  when the lane publishes no health. */
export const laneServedRequests = (lane: DiscoveryLane): number =>
  lane.health?.servedRequests ?? 0;

/** What a platform actually served, summed across every lane of every SKU. */
const servedRequests = (skus: RawCatalogEntry[]): number =>
  skus.reduce(
    (total, sku) => total + sku.lanes.reduce((s, lane) => s + laneServedRequests(lane), 0),
    0,
  );

const KNOWN_CATEGORIES = new Set<CategoryId>([
  'all',
  'enrichment',
  'social',
  'music',
  'video',
  'shop',
  'search',
  'fin',
  'travel',
  'data',
]);

/** Coerces a gateway category string to a known CategoryId ('data' fallback). */
export const toCategoryId = (c: string): CategoryId =>
  KNOWN_CATEGORIES.has(c as CategoryId) ? (c as CategoryId) : 'data';

/** Task-first cards are merchandised together without changing gateway discovery categories. */
const catalogCategory = (entry: RawCatalogEntry): CategoryId =>
  ENRICHMENT_PLATFORM_IDS.has(platformKey(entry.slug))
    ? 'enrichment'
    : toCategoryId(entry.category);

/** The English action title a live endpoint shows: "tiktok.profile_videos" -> "Profile Videos". */
export const liveActionOf = (slug: string): string => titleCase(actionOf(slug));

const describeOutcomes = (outcomes: CatalogOutcome[]): string =>
  outcomes.map((outcome) => `${outcome.title}: ${outcome.examples}`).join('; ');

const catalogSearchText = (
  meta: { id: string; name: string },
  category: CategoryId,
  endpoint: string,
  outcomes: CatalogOutcome[],
  skus: RawCatalogEntry[],
): string =>
  [
    meta.id,
    meta.name,
    category,
    CNAME[category],
    endpoint,
    ...outcomes.flatMap((outcome) => [outcome.title, outcome.examples]),
    ...skus.flatMap((sku) => [sku.slug, sku.name, actionOf(sku.slug), sku.description]),
  ].join(' ');

/** The canonical display id, name, and icon for the platform a SKU slug belongs
 *  to. Reuses the same PLATFORM map + fallback naming the catalog cards use, so
 *  the twitter -> x-twitter (and similar) alias never drifts. */
export const platformInfoOf = (
  slug: string,
): { id: string; name: string; icon: string } => platformMeta(platformKey(slug));

export const operationForSku = (entries: RawCatalogEntry[], slug: string): RawCatalogEntry => {
  const entry = entries.find((item) => item.slug === slug);
  if (!entry) throw new CatalogAuthorityError(`Live discovery is missing SKU "${slug}"`);
  return entry;
};

/** Groups SKU entries into per-platform catalog cards. */
export const groupCatalog = (entries: RawCatalogEntry[]): CatalogApi[] => {
  const groups = new Map<string, RawCatalogEntry[]>();
  for (const e of entries) {
    const key = platformKey(e.slug);
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(e);
  }

  const cards: CatalogApi[] = [];
  for (const [key, skus] of groups) {
    const sortedSkus = [...skus].sort((a, b) => a.slug.localeCompare(b.slug));
    const meta = platformMeta(key);
    const category = catalogCategory(sortedSkus[0]);
    // Platform cards aggregate the authoritative per-SKU `pricing.from` offers.
    // They never choose a different lane-level price.
    const quote = lowestHeadlineQuote(sortedSkus);
    const providers = sourcesForEntries(sortedSkus);
    const n = sortedSkus.length;
    const endpoint = `${n} endpoint${n > 1 ? 's' : ''}`;
    const outcomes = resolveCatalogOutcomes(key, meta.name, sortedSkus);

    cards.push({
      id: meta.id,
      name: meta.name,
      category,
      icon: meta.icon,
      endpoint,
      outcomes,
      searchText: catalogSearchText(meta, category, endpoint, outcomes, sortedSkus),
      description: describeOutcomes(outcomes),
      providers,
      price: quote.price,
      endpointCount: n,
      requests: servedRequests(sortedSkus),
      detailHref: `/api/${meta.id}`,
    });
  }

  return cards.sort((a, b) => a.name.localeCompare(b.name));
};

/** Maps the live catalog (one entry per SKU) into per-SKU rows for the command
 *  palette's instant keyword filter. Mirrors the CatalogSearchResult the gateway
 *  returns for use-case mode (relevance defaults to 0; no field highlights), so the
 *  palette renders keyword and semantic results through one row grammar. */
export const toSearchRows = (entries: RawCatalogEntry[]): CatalogSearchResult[] =>
  entries.map((e) => ({
    slug: e.slug,
    platformId: platformIdOf(e.slug),
    name: e.name,
    description: e.description,
    method: e.method,
    path: e.path,
    execution: e.execution,
    category: catalogCategory(e),
    provider: e.provider,
    pricing: e.pricing,
    tryMaxItems: e.tryMaxItems,
    failover: e.failover,
    ...(e.excludesCallerDelay === undefined
      ? {}
      : { excludesCallerDelay: e.excludesCallerDelay }),
    relevance: 0,
  }));

/** The catalog categories that actually have SKUs (plus "All"), in CATS order. */
export const presentCategories = (entries: RawCatalogEntry[]): Category[] => {
  const present = new Set(entries.map(catalogCategory));
  return CATS.filter((c) => c.id === 'all' || present.has(c.id)).map((c) => ({
    ...c,
    blurb: catalogText(c.blurb),
  }));
};

/** One live endpoint per SKU, with its gateway-authored operation and one public
 *  source identity per enabled lane. Upstream routing slugs never appear.
 *  Latency/uptime/requests are each lane's measured 30d health from the
 *  gateway. Metric-specific samples remain undefined against an older gateway,
 *  which preserves the old visible value without inventing a sample label. */
const liveEndpoint = (e: RawCatalogEntry): Endpoint => {
  return {
    id: endpointIdOf(e.slug),
    action: liveActionOf(e.slug),
    method: e.method,
    path: e.path,
    description: e.description,
    sku: e.slug,
    tryEligible: e.tryEligible,
    tryMaxItems: e.tryMaxItems,
    failover: hasFailover(e),
    providers: e.lanes.map((q, i) => laneQuote(e.slug, q, i)),
    ...(e.notServingSources === undefined || e.notServingSources.length === 0
      ? {}
      : {
          notServing: e.notServingSources.map((q, i) =>
            // Offset past the serving lanes so the pre-source-identity fallback
            // cannot hand a withdrawn source the same animal as a serving one.
            laneQuote(e.slug, q, e.lanes.length + i)),
        }),
  };
};

/** One source row: its price, its measured health, and the identity it is shown
 *  under. `index` keeps the row id unique within the endpoint. */
const laneQuote = (slug: string, lane: DiscoveryLane, index: number): ProviderQuote => ({
  id: `${slug}#${index}`,
  source: lane.source,
  price: displayPrice(lane.pricing),
  latencyMs: lane.health?.latencyP50Ms ?? 0,
  uptimePct: lane.health?.uptimePct ?? 0,
  requests: laneServedRequests(lane),
  servedRequests: lane.health?.servedRequests,
  uptimeSample: lane.health?.uptimeSample,
  ...(lane.health?.blockedPct === undefined ? {} : { blockedPct: lane.health.blockedPct }),
  latencySample: lane.health?.latencySample,
  trafficSharePct: index === 0 ? 100 : 0,
});

/** Builds an ApiDetail for a platform straight from the live catalog - the
 *  detail page for the many platforms that have no hand-written copy. The
 *  routeId is the /api/<id> segment (e.g. "tiktok-shop"). Null when the
 *  platform has no SKUs. */
export const liveApiDetail = (entries: RawCatalogEntry[], routeId: string): ApiDetail | null => {
  const key = platformKeyForRoute(routeId);
  const skus = entries.filter((e) => platformKey(e.slug) === key);
  if (skus.length === 0) return null;
  const meta = platformMeta(key);
  const category = catalogCategory(skus[0]);
  return {
    id: routeId,
    name: meta.name,
    category,
    color: CCOLOR[category],
    // The failover half comes only from the gateway's published routing fact.
    blurb: (skus.some(hasFailover) ? m.live_platform_blurb_failover : m.live_platform_blurb)({
      name: meta.name,
    }),
    endpoints: skus.map(liveEndpoint),
  };
};

/** Reconciles hand-written copy with authoritative live operational facts.
 *  Covered endpoints retain their authored examples and descriptions while
 *  pricing, lanes, health, eligibility, and failover always come from discovery. */
export const mergeLiveEndpoints = (
  detail: EditorialApiDetail,
  entries: RawCatalogEntry[],
  routeId: string,
): ApiDetail => {
  const live = liveApiDetail(entries, routeId);
  if (!live) {
    throw new CatalogAuthorityError(
      `Published API "${routeId}" has no live discovery record`,
    );
  }
  const liveBySku = new Map(
    live.endpoints.map((e) => [e.sku, e]),
  );
  const endpoints = detail.endpoints.map((e) => {
    const l = liveBySku.get(e.sku);
    if (!l) {
      throw new CatalogAuthorityError(
        `Published endpoint "${routeId}/${e.id}" is missing live SKU "${e.sku}"`,
      );
    }
    return {
      ...e,
      method: l.method,
      path: l.path,
      providers: l.providers,
      ...(l.notServing === undefined ? {} : { notServing: l.notServing }),
      tryEligible: l.tryEligible,
      tryMaxItems: l.tryMaxItems,
      failover: l.failover,
    };
  });
  const covered = new Set(detail.endpoints.map((e) => e.sku));
  const extra = live.endpoints.filter((e) => !covered.has(e.sku));
  return { ...detail, endpoints: [...endpoints, ...extra] };
};

class CatalogAuthorityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CatalogAuthorityError';
  }
}
